import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Loader2, UserPlus, Mail, Copy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hdr = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const errMsg = (e, d) => (typeof e.response?.data?.detail === 'string' ? e.response.data.detail : d);

const MyEmailCard = ({ email, onChanged }) => {
  const [form, setForm] = useState({ new_email: '', current_password: '' });
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg(null);
    try {
      const { data } = await axios.put(`${API}/admin/me/email`, form, hdr());
      const u = JSON.parse(localStorage.getItem('userData') || '{}');
      localStorage.setItem('userData', JSON.stringify({ ...u, email: data.email }));
      setForm({ new_email: '', current_password: '' });
      setMsg({ ok: true, text: data.message });
      onChanged();
    } catch (err) { setMsg({ ok: false, text: errMsg(err, 'Erro ao alterar e-mail') }); }
    setSaving(false);
  };
  return (
    <Card data-testid="admin-my-email-card">
      <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><Mail className="w-5 h-5" />Meu e-mail de acesso</CardTitle></CardHeader>
      <CardContent>
        <p className="text-sm text-gray-600 mb-4">E-mail atual: <strong data-testid="admin-current-email">{email}</strong></p>
        <form onSubmit={submit} className="grid gap-3 md:grid-cols-3 items-end">
          <div><Label>Novo e-mail</Label><Input type="email" required value={form.new_email} onChange={(e) => setForm({ ...form, new_email: e.target.value })} data-testid="admin-new-email-input" /></div>
          <div><Label>Senha atual (confirmação)</Label><Input type="password" required value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} data-testid="admin-email-password-input" /></div>
          <Button type="submit" disabled={saving} data-testid="admin-email-submit">{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Salvar novo e-mail</Button>
        </form>
        {msg && <p data-testid="admin-email-msg" className={`text-sm mt-3 ${msg.ok ? 'text-green-700' : 'text-red-600'}`}>{msg.text}</p>}
      </CardContent>
    </Card>
  );
};

const NewAdminForm = ({ onCreated }) => {
  const [form, setForm] = useState({ nome: '', email: '' });
  const [created, setCreated] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError(''); setCreated(null);
    try {
      const { data } = await axios.post(`${API}/admin/admins`, form, hdr());
      setCreated({ email: data.admin.email, password: data.temp_password });
      setForm({ nome: '', email: '' });
      onCreated();
    } catch (err) { setError(errMsg(err, 'Erro ao criar administrador')); }
    setSaving(false);
  };
  return (
    <Card data-testid="admin-new-admin-card">
      <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><UserPlus className="w-5 h-5" />Novo administrador</CardTitle></CardHeader>
      <CardContent>
        <form onSubmit={submit} className="grid gap-3 md:grid-cols-3 items-end">
          <div><Label>Nome</Label><Input required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="new-admin-name-input" /></div>
          <div><Label>E-mail</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-admin-email-input" /></div>
          <Button type="submit" disabled={saving} data-testid="new-admin-submit">{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Criar administrador</Button>
        </form>
        {error && <p data-testid="new-admin-error" className="text-sm text-red-600 mt-3">{error}</p>}
        {created && (
          <div data-testid="new-admin-temp-box" className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm">
            <p>Administrador <strong>{created.email}</strong> criado. Senha provisória (exibida só agora; ele deverá trocá-la no primeiro acesso):</p>
            <div className="flex items-center gap-2 mt-2">
              <code data-testid="new-admin-temp-password" className="px-2 py-1 bg-white border rounded font-mono">{created.password}</code>
              <Button type="button" size="sm" variant="outline" onClick={() => navigator.clipboard?.writeText(created.password)} data-testid="new-admin-copy-password"><Copy className="w-4 h-4 mr-1" />Copiar</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const AdminRow = ({ a, me, onToggle }) => (
  <tr data-testid={`admin-row-${a.id}`} className="border-t">
    <td className="py-2 pr-3">{a.nome || '—'}{a.id === me && <span className="ml-2 text-xs text-gray-500">(você)</span>}</td>
    <td className="py-2 pr-3">{a.email}</td>
    <td className="py-2 pr-3">
      <span data-testid={`admin-status-${a.id}`} className={a.active ? 'text-green-700' : 'text-red-600'}>{a.active ? 'Ativo' : 'Desativado'}</span>
      {a.active && a.must_change_password && <span className="ml-2 text-xs text-amber-700">aguardando 1º acesso</span>}
    </td>
    <td className="py-2 text-right">
      {a.id !== me && (
        <Button size="sm" variant="outline" onClick={() => onToggle(a)} data-testid={`admin-toggle-${a.id}`}>{a.active ? 'Desativar' : 'Reativar'}</Button>
      )}
    </td>
  </tr>
);

export const AdminUsers = () => {
  const [data, setData] = useState({ me: null, admins: [] });
  const load = () => axios.get(`${API}/admin/admins`, hdr()).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const toggle = async (a) => {
    if (!window.confirm(`${a.active ? 'Desativar' : 'Reativar'} o acesso de ${a.email}?`)) return;
    try { await axios.put(`${API}/admin/admins/${a.id}/active`, { active: !a.active }, hdr()); load(); }
    catch (err) { alert(errMsg(err, 'Erro ao atualizar')); }
  };
  const myEmail = data.admins.find((a) => a.id === data.me)?.email || '';
  return (
    <div data-testid="admin-users-page" className="space-y-6">
      <h1 className="text-2xl font-bold">Administradores</h1>
      <MyEmailCard email={myEmail} onChanged={load} />
      <NewAdminForm onCreated={load} />
      <Card>
        <CardHeader><CardTitle className="text-lg">Administradores cadastrados</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full text-sm" data-testid="admin-list-table">
            <thead><tr className="text-left text-gray-500"><th className="pb-2">Nome</th><th className="pb-2">E-mail</th><th className="pb-2">Status</th><th /></tr></thead>
            <tbody>{data.admins.map((a) => <AdminRow key={a.id} a={a} me={data.me} onToggle={toggle} />)}</tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};
